import { useLocation } from "react-router";

import { useSession } from "../../features/auth/hooks";
import { useIsDesktop } from "../../lib/useIsDesktop";

import { AppHeader } from "./AppHeader";
import { Backdrop } from "./Backdrop";
import { BottomTabBar } from "./BottomTabBar";
import { DesktopRightRail } from "./DesktopRightRail";
import { DesktopSidebar } from "./DesktopSidebar";
import { DesktopTopBar } from "./DesktopTopBar";

export function AppShell({ children }: { children: React.ReactNode }) {
  const isDesktop = useIsDesktop();
  return (
    <>
      <Backdrop />
      {isDesktop ? <DesktopShell>{children}</DesktopShell> : <MobileShell>{children}</MobileShell>}
    </>
  );
}

function DesktopShell({ children }: { children: React.ReactNode }) {
  // /community owns the full feed in its main column, so the rail renders
  // nothing there. Collapse the grid to two tracks and drop the slot to
  // display:none so no empty 320px column (a phantom bordered void) remains.
  // display:none keeps DesktopRightRail mounted, preserving its enabled-false
  // query behavior across navigation (it never refetches on a remount).
  const onCommunity = useLocation().pathname === "/community";
  return (
    <div data-testid="desktop-grid" className="min-h-screen">
      <header
        data-testid="topbar-slot"
        className="h-14 border-b border-line bg-bg"
      >
        <DesktopTopBar />
      </header>
      <div className={onCommunity ? "grid grid-cols-[240px_1fr]" : "grid grid-cols-[240px_1fr_320px]"}>
        <aside data-testid="sidebar-slot" className="border-r border-line bg-bg">
          <DesktopSidebar />
        </aside>
        <main data-testid="main-slot" className="min-h-[calc(100vh-3.5rem)] px-6 py-4">
          {children}
        </main>
        <div
          data-testid="right-rail-slot"
          className={onCommunity ? "hidden" : "border-l border-line bg-bg"}
        >
          <DesktopRightRail />
        </div>
      </div>
    </div>
  );
}

function MobileShell({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  return (
    <main className="mobile-frame relative mx-auto flex min-h-screen max-w-[430px] flex-col bg-bg md:border-x md:border-line">
      <AppHeader user={user} />
      <div className="flex-1 px-5 pb-[88px] pt-2">{children}</div>
      <BottomTabBar />
    </main>
  );
}
